<template>
  <ReportCard :title="$t('report.clients_overview_title')" header-margin="mb-4">
    <template #header>
      <button @click.stop="showOverviewInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
        :title="$t('report.clients_overview_info.title')">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>

    <!-- Hero: Total + evolution -->
    <div class="mb-3 pb-3 border-b border-foreground/[0.06]">
      <div class="flex items-baseline justify-between mb-2.5">
        <div>
          <p class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 mb-0.5">
            {{ $t('report.clients_total') }}
          </p>
          <span class="text-[32px] font-extrabold text-foreground leading-none tracking-tight">
            {{ response!.overview.total_clients }}
          </span>
        </div>
        <EvolutionIndicator
          v-if="hasComparison && equitablePreviousTotalClients != null"
          :value="totalClientsEvolution"
          :formatted="totalClientsEvolutionFormatted"
          :show-neutral="true" />
      </div>

      <!-- New / Returning structured breakdown -->
      <div class="flex w-full h-1 rounded-full overflow-hidden mb-2">
        <span class="h-full" style="background-color: #10B981"
          :style="{ width: (response!.overview.new_ratio * 100) + '%' }"></span>
        <span class="h-full" style="background-color: #6366F1"
          :style="{ width: ((1 - response!.overview.new_ratio) * 100) + '%' }"></span>
      </div>
      <div class="grid grid-cols-2 gap-2">
        <div class="ct-member-cell rounded-lg px-2.5 py-1.5">
          <div class="flex items-center gap-1.5 mb-0.5">
            <span class="w-1.5 h-1.5 rounded-full" style="background: #10B981"></span>
            <span class="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
              {{ $t('report.clients_new') }}
            </span>
          </div>
          <div class="flex items-baseline gap-1.5">
            <span class="text-[17px] font-bold text-foreground leading-none">
              {{ response!.overview.new_clients }}
            </span>
            <EvolutionIndicator v-if="hasComparison && newClientsEvolution !== 0"
              :value="newClientsEvolution"
              :formatted="newClientsEvolutionFormatted" />
          </div>
        </div>
        <div class="ct-member-cell rounded-lg px-2.5 py-1.5">
          <div class="flex items-center gap-1.5 mb-0.5">
            <span class="w-1.5 h-1.5 rounded-full" style="background: #6366F1"></span>
            <span class="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
              {{ $t('report.clients_returning') }}
            </span>
          </div>
          <div class="flex items-baseline gap-1.5">
            <span class="text-[17px] font-bold text-foreground leading-none">
              {{ response!.overview.returning_clients }}
            </span>
            <EvolutionIndicator v-if="hasComparison && returningClientsEvolution !== 0"
              :value="returningClientsEvolution"
              :formatted="returningClientsEvolutionFormatted" />
          </div>
        </div>
      </div>
    </div>

    <!-- Secondary KPIs: 2x2 compact grid -->
    <div class="grid grid-cols-2 gap-x-4 gap-y-3">
      <div>
        <p class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-0.5">
          {{ $t('report.clients_avg_revenue') }}
        </p>
        <span class="text-[18px] font-bold text-foreground">
          {{ formatCentimes(response!.overview.avg_revenue_per_client) }}
        </span>
      </div>
      <div>
        <p class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-0.5">
          {{ $t('report.clients_avg_basket') }}
        </p>
        <span class="text-[18px] font-bold text-foreground">
          {{ formatCentimes(response!.overview.avg_basket) }}
        </span>
      </div>
      <div>
        <p class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-0.5">
          {{ $t('report.clients_avg_purchases') }}
        </p>
        <span class="text-[18px] font-bold text-foreground">
          {{ response!.overview.avg_purchases_per_client != null ? response!.overview.avg_purchases_per_client.toFixed(1) + 'x' : '-' }}
        </span>
      </div>
      <div v-if="hasComparison && response!.overview.retention_rate != null">
        <p class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-0.5">
          {{ $t('report.clients_retention_rate') }}
        </p>
        <span class="text-[18px] font-bold text-foreground">
          {{ formatRatio(response!.overview.retention_rate) }}
        </span>
      </div>
    </div>
  </ReportCard>

  <BottomSheet v-model:is-open="showOverviewInfo" variant="sidebar">
    <ClientsOverviewInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { SalesByClientsResponse } from '../../responses'
import { useAppSettingsStore } from '../../stores/AppSettingsStore'
import { formatCurrencyForCenter } from '../../utils/Languages'
import { trimToPresent } from '../../utils/Projection'
import ReportCard from '../ReportCard.vue'
import EvolutionIndicator from '../ui/EvolutionIndicator.vue'
import BottomSheet from '../BottomSheet.vue'
import ClientsOverviewInfoSheet from '../ClientsOverviewInfoSheet.vue'

const props = defineProps<{
  response: SalesByClientsResponse | null
  centerId: number
}>()

const appSettingsStore = useAppSettingsStore()
const centerObj = computed(() => appSettingsStore.getCenter(props.centerId))
const showOverviewInfo = ref(false)

const hasComparison = computed(() => props.response?.metadata.has_comparison === true)

// --- Helpers ---

function formatCentimes(val: number | null | undefined): string {
  if (val == null) return '-'
  return formatCurrencyForCenter(val, centerObj.value)
}

function formatRatio(ratio: number | null | undefined): string {
  if (ratio == null) return '-'
  return `${(ratio * 100).toFixed(1)}%`
}

// --- KPI evolution ---

const equitableCutoff = computed(() => {
  if (!props.response?.intervals.length) return 0
  return trimToPresent(props.response.intervals.map(i => i.data.cumulative.total_clients))
})

const prevAtCutoff = computed(() => {
  const cutoff = equitableCutoff.value
  const prev = props.response?.previous_intervals
  if (!cutoff || !prev?.length) return null
  const idx = Math.min(cutoff - 1, prev.length - 1)
  return prev[idx]?.data.cumulative ?? null
})

function evoPercent(current: number | null | undefined, previous: number | null | undefined): number {
  if (current == null || previous == null || previous === 0) return 0
  return ((current - previous) / previous) * 100
}

function formatEvo(evo: number): string {
  if (evo === 0) return '0 %'
  return `${evo > 0 ? '+' : ''}${evo.toFixed(1)} %`
}

const totalClientsEvolution = computed(() =>
  evoPercent(props.response?.overview.total_clients, prevAtCutoff.value?.total_clients))
const totalClientsEvolutionFormatted = computed(() => formatEvo(totalClientsEvolution.value))

const newClientsEvolution = computed(() =>
  evoPercent(props.response?.overview.new_clients, prevAtCutoff.value?.new_clients))
const newClientsEvolutionFormatted = computed(() => formatEvo(newClientsEvolution.value))

const returningClientsEvolution = computed(() =>
  evoPercent(props.response?.overview.returning_clients, prevAtCutoff.value?.returning_clients))
const returningClientsEvolutionFormatted = computed(() => formatEvo(returningClientsEvolution.value))

const equitablePreviousTotalClients = computed(() => prevAtCutoff.value?.total_clients ?? null)
</script>

<style scoped>
.ct-member-cell {
  background: rgba(0, 0, 0, 0.02);
}
:is(.dark *).ct-member-cell {
  background: rgba(255, 255, 255, 0.03);
}
</style>
